import React, { useEffect, useState, useRef } from "react";
import { LuBell } from "react-icons/lu";
import { IoClose } from "react-icons/io5";
import api from '../../api/axios';
import { jwtDecode } from "jwt-decode";
import { BACKEND_API_URL } from '../../config/api';
import styles from "./NotificationButton.module.css";

export const NotificationButton = ({ isCollapsed = false, onPlayerRefresh }) => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const unreadCount = notifications.filter((notification) => !notification.is_read).length;

  useEffect(() => {
    // fetch userid from jwtToken
    const token = localStorage.getItem("jwtToken");
    if (!token) {
      console.error("No JWT token found.");
      return;
    }
    const decodedToken = jwtDecode(token);
    const user_id = decodedToken.user_id;

    // Build SSE endpoint URL using shared config
    const SSE_URL = `${BACKEND_API_URL}/notifications/stream/${user_id}`;

    // create EventSource connection
    const eventSource = new EventSource(SSE_URL);

    // Listen for incoming messages
    eventSource.onmessage = (event) => {
      // Parse the JSON data sent from BACKEND
      const eventNotification = JSON.parse(event.data);

      // store received notifications in state
      if (eventNotification) {
        setNotifications((prevNotifications) => {
          const updatedNotifications = [
            { ...eventNotification, is_read: false },
            ...prevNotifications,
          ];
          return updatedNotifications;
        });
      }
      
      // If player had been added || updated update the users table
      if (eventNotification.notification_type === 'player_added' ||
        eventNotification.notification_type === 'player_updated') {
          // trigger player table refresh
          if (onPlayerRefresh) {
            onPlayerRefresh();
          }
        }
    };

    // Handle connection errors
    eventSource.onerror = (error) => {
      eventSource.close();
    };

    // Cleanup when component unmounts
    return () => {
      eventSource.close();
    };
  }, []); // empty dependency array = run once on mount

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      setNotifications((prevNotifications) => (
        prevNotifications.map((notification) => (
          notification.notification_id === notificationId
            ? { ...notification, is_read: true }
            : notification
        ))
      ));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const deleteNotification = async (notificationId, event) => {
    event.stopPropagation(); // Prevent dropdown from closing
    
    try {
      await api.delete(`/notifications/${notificationId}`);

      // Remove notification from local state
      setNotifications((prevNotifications) => {
        return prevNotifications.filter(
          (notif) => notif.notification_id !== notificationId
        );
      });
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className={styles.notificationContainer} ref={dropdownRef}>
      <button className={styles.notificationButton} onClick={toggleDropdown}>
        <LuBell className={styles.icon} />
        {!isCollapsed && <span style={{marginLeft: "4px"}}>Notifications</span>}
        {unreadCount > 0 && (
          <span className={`${styles.badge} ${isCollapsed ? styles.badgeCollapsed : styles.badge}`}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <span className={styles.unreadLabel}>{unreadCount} new</span>
            )}
          </div>

          <div className={styles.notificationList}>
            {notifications.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.notification_id}
                  className={`${styles.notificationItem} ${notif.is_read ? styles.notificationRead : styles.notificationUnread}`}
                  onClick={() => {
                    if (!notif.is_read) {
                      markNotificationAsRead(notif.notification_id);
                    }
                  }}
                  role={!notif.is_read ? "button" : undefined}
                  tabIndex={!notif.is_read ? 0 : undefined}
                  onKeyDown={(event) => {
                    if (!notif.is_read) {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        markNotificationAsRead(notif.notification_id);
                      }
                    }
                  }}
                >
                  <div className={styles.notificationContent}>
                    <p className={styles.notificationText}>{notif.notification_text}</p>
                    <span className={styles.notificationTime}>
                      {formatDate(notif.created_at)}
                    </span>
                  </div>
                  <div className={styles.notificationActions}>
                    <button
                      className={styles.deleteButton}
                      onClick={(e) => deleteNotification(notif.notification_id, e)}
                      aria-label="Delete notification"
                    >
                      <IoClose />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
